import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FETCH_SIZE } from "../../../utilities/constants";
import {
  Gender,
  useGetCaregiversAsAdminQuery,
  useSearchCaregiversAsAdminLazyQuery,
} from "../../../lib/apollo/graphql/generated";

export type UserSearchFilters = {
  gender: Gender | null;
  query: string;
  city: string;
  state: string;
};

const useCaregiverList = () => {
  const navigate = useNavigate();

  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [filters, setFilters] = useState<UserSearchFilters>({
    query: "",
    gender: null,
    state: "",
    city: "",
  });

  const { data, loading, fetchMore } = useGetCaregiversAsAdminQuery({
    variables: {
      first: FETCH_SIZE,
    },
  });

  const [
    searchCaregivers,
    {
      data: searchData,
      loading: searchLoading,
      fetchMore: fetchMoreSearchResults,
    },
  ] = useSearchCaregiversAsAdminLazyQuery();

  /**
   * Handler for when user searches for specific set of caregivers
   */
  const onSearch = () => {
    if (!filters.query) {
      onCancelSearch();
      return;
    }

    setIsSearched(true);

    searchCaregivers({
      variables: filters,
    });
  };

  const onCancelSearch = () => {
    setIsSearched(false);
    setFilters({
      query: "",
      gender: null,
      state: "",
      city: "",
    });
  };

  /**
   * Handler for when search filter values change
   */
  const onFilterChange = <K extends keyof UserSearchFilters>(
    key: K,
    value: UserSearchFilters[K]
  ) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  /**
   * - Handler for when user clicks a user row
   * - Redirects user to user detail page
   */
  const onRouteToUserDetailPage = (userId?: string | number) => {
    if (userId) {
      navigate(`/detail/caregivers/${userId}`);
    }
  };

  /**
   * Handler for when intersection observer's inView status changes
   */
  const onViewChange = (inView: boolean) => {
    if (inView) {
      setIsFetchingMore(true);

      if (isSearched) {
        fetchMoreSearchResults({
          variables: {
            first: FETCH_SIZE,
            after: searchData?.searchCaregiversAsAdmin.pageInfo.endCursor,
            ...filters,
          },
        });
      } else {
        fetchMore({
          variables: {
            first: FETCH_SIZE,
            after: data?.getCaregiversAsAdmin.pageInfo.endCursor,
          },
        });
      }

      setIsFetchingMore(false);
    }
  };

  return {
    models: {
      isFetchingMore,
      isSearched,
      loading: searchLoading || loading,
      filters,
      data: isSearched
        ? searchData?.searchCaregiversAsAdmin.edges
        : data?.getCaregiversAsAdmin.edges,
      pageInfo: isSearched
        ? searchData?.searchCaregiversAsAdmin.pageInfo
        : data?.getCaregiversAsAdmin.pageInfo,
    },
    operations: {
      onRouteToUserDetailPage,
      onSearch,
      onCancelSearch,
      onFilterChange,
      onViewChange,
    },
  };
};

export default useCaregiverList;
